/**
 * Course Comments Component
 * Allows logged-in users to comment on courses
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Avatar,
  Alert,
  CircularProgress,
  Divider,
  Link,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import LoginIcon from '@mui/icons-material/Login';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchCourseComments, addComment, deleteComment } from '../../store/thunks/commentsThunks';
import { Course, CourseComment } from '@e-learning/shared/src/store/types';
import LoginModal from '../auth/LoginModal';

interface CourseCommentsProps {
  course: Course;
}

const CourseComments: React.FC<CourseCommentsProps> = ({ course }) => {
  const dispatch = useAppDispatch();
  const { user, isAuthenticated } = useAppSelector((state) => state.auth);
  const { courseComments, loading, error } = useAppSelector((state) => state.comments);
  
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loginModalOpen, setLoginModalOpen] = useState(false);

  const comments = courseComments[course.id] || [];

  useEffect(() => {
    dispatch(fetchCourseComments(course.id));
  }, [course.id, dispatch]);

  const handleSubmitComment = async () => {
    if (!isAuthenticated || !user || !commentText.trim()) {
      return;
    }

    setSubmitting(true);
    try {
      await dispatch(
        addComment({
          courseId: course.id,
          userId: user.uid,
          userEmail: user.email,
          userName: user.displayName || user.email || 'Anonymous',
          comment: commentText,
        })
      ).unwrap();
      setCommentText('');
    } catch (err) {
      console.error('Failed to add comment:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!isAuthenticated || !user) {
      return;
    }

    if (!window.confirm('Are you sure you want to delete this comment?')) {
      return;
    }

    try {
      await dispatch(deleteComment({ courseId: course.id, commentId })).unwrap();
    } catch (err) {
      console.error('Failed to delete comment:', err);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
        Comments ({comments.length})
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Comment Form */}
      {isAuthenticated ? (
        <Paper
          sx={{
            p: 3,
            mb: 4,
            borderRadius: 3,
            background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.9) 100%)',
            border: '1px solid rgba(148, 163, 184, 0.1)',
          }}
        >
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            Add a comment
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            placeholder="Share your thoughts about this course..."
            variant="outlined"
            disabled={submitting}
            sx={{ mb: 2 }}
          />
          <Button
            variant="contained"
            startIcon={submitting ? <CircularProgress size={20} /> : <SendIcon />}
            onClick={handleSubmitComment}
            disabled={!commentText.trim() || submitting}
            sx={{
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
              },
            }}
          >
            Post Comment
          </Button>
        </Paper>
      ) : (
        <Alert 
          severity="info" 
          sx={{ 
            mb: 4,
            '& .MuiAlert-message': {
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            },
          }}
        >
          <Typography variant="body2">
            Please{' '}
            <Link
              component="button"
              variant="body2"
              onClick={() => setLoginModalOpen(true)}
              sx={{
                color: 'primary.light',
                textDecoration: 'underline',
                fontWeight: 600,
                cursor: 'pointer',
                '&:hover': {
                  color: 'primary.main',
                },
              }}
            >
              log in
            </Link>
            {' '}to add a comment.
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<LoginIcon />}
            onClick={() => setLoginModalOpen(true)}
            sx={{
              ml: 2,
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
              },
            }}
          >
            Login
          </Button>
        </Alert>
      )}

      {/* Comments List */}
      {loading && comments.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : comments.length === 0 ? (
        <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          No comments yet. Be the first to comment!
        </Typography>
      ) : (
        <Box>
          {comments.map((comment, index) => (
            <React.Fragment key={comment.id}>
              <Paper
                sx={{
                  p: 3,
                  mb: 2,
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.9) 100%)',
                  border: '1px solid rgba(148, 163, 184, 0.1)',
                }}
              >
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    {comment.userName?.[0]?.toUpperCase() || comment.userEmail?.[0]?.toUpperCase() || 'A'}
                  </Avatar>
                  <Box sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {comment.userName || comment.userEmail || 'Anonymous'}
                      </Typography>
                      {isAuthenticated && user && comment.userId === user.uid && (
                        <Button
                          size="small"
                          color="error"
                          startIcon={<DeleteIcon />}
                          onClick={() => handleDeleteComment(comment.id)}
                        >
                          Delete
                        </Button>
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                      {formatDate(comment.createdAt)}
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {comment.comment}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
              {index < comments.length - 1 && <Divider sx={{ my: 2 }} />}
            </React.Fragment>
          ))}
        </Box>
      )}

      <LoginModal
        open={loginModalOpen}
        onClose={() => setLoginModalOpen(false)}
        onSuccess={() => {
          // User successfully logged in, modal will close automatically
        }}
      />
    </Box>
  );
};

export default CourseComments;

